import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Trash2, Copy, Download, Calendar, MapPin, FileText, Loader2, ScrollText,
  Users, User, ArrowUpNarrowWide, ArrowDownNarrowWide, PenLine, MessageSquareQuote,
  LayoutGrid, GalleryVerticalEnd, MessagesSquare, CornerDownRight, Link2, SlidersHorizontal, X, Palette,
  Keyboard, Zap, Image as ImageIcon, RotateCw, Lightbulb, Check, Send, ZoomIn, ZoomOut, Sparkles, Heart, Printer, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, BookOpen, Quote,
} from "lucide-react";
import { api } from "../lib/api";
import { ScrollToTop } from "../components/ScrollToTop";
import { useAuth } from "../context/AuthContext";
import { rotateDataUrl, fileToResizedDataUrl } from "../lib/imageUtils";
import { DOC_TYPE_LABEL } from "../data/samples";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const PAGE_MARK_RE = /\s*<<<PAGINA>>>\s*/g;
const stripMarkers = (t) => (t || "").replace(/[⟦⟧]/g, "").replace(PAGE_MARK_RE, "\n\n");
const countUnreadable = (text) => {
  const re = /(?<![0-9A-Za-zÀ-ÿ])x{2,}(?![0-9A-Za-zÀ-ÿ])/gi;
  const m = (text || "").replace(/[⟦⟧]/g, "").match(re);
  return m ? m.length : 0;
};
function UnreadableBadge({ count, className = "" }) {
  if (!count) return null;
  return (
    <span
      data-testid="unreadable-badge"
      title={`${count} onleesbaar woord${count === 1 ? "" : "en"} — lezers kunnen hier een suggestie doen`}
      className={`inline-flex items-center gap-1 rounded-full bg-[#dc2626] px-2.5 py-1 text-xs font-extrabold uppercase tracking-wide text-white shadow-lg ring-2 ring-white/80 ${className}`}
    >
      <Lightbulb className="h-3.5 w-3.5" /> {count} onleesbaar
    </span>
  );
}
function HighlightTags({ items, className = "", max = 6 }) {
  if (!items || !items.length) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${className}`} data-testid="highlight-tags">
      {items.slice(0, max).map((h, i) => (
        <span
          key={i}
          data-testid={`highlight-tag-${i}`}
          className="inline-flex items-center gap-1 rounded-full border border-[var(--brand)]/30 bg-[var(--brand)]/10 px-2 py-0.5 text-[11px] font-semibold capitalize text-[var(--brand)]"
        >
          <Sparkles className="h-3 w-3" /> {h}
        </span>
      ))}
    </div>
  );
}

function SearchSnippet({ letter, term, testid }) {
  if (!term) return null;
  const clean = stripMarkers(letter.transcription || "");
  const idx = clean.toLowerCase().indexOf(term);
  if (idx === -1) return null;
  const start = Math.max(0, idx - 40);
  const end = Math.min(clean.length, idx + term.length + 70);
  return (
    <p data-testid={testid} className="mt-2 rounded-md border border-[var(--amber-border)]/50 bg-[var(--amber-bg)] px-2 py-1 text-xs leading-relaxed text-[var(--ink-2)]">
      <span className="mr-1 font-mono text-[9px] uppercase tracking-wider text-[var(--amber-text)]">Gevonden in tekst</span>
      {start > 0 && "…"}{clean.slice(start, idx)}
      <mark className="rounded bg-[#dc2626] px-0.5 font-semibold text-white">{clean.slice(idx, idx + term.length)}</mark>
      {clean.slice(idx + term.length, end)}{end < clean.length && "…"}
    </p>
  );
}

const DOC_ICONS = { handgeschreven: PenLine, getypt: Keyboard, ansichtkaart: ImageIcon, telegram: Zap, briefpapier: FileText };
const coverOf = (l) => l.thumbnail || l.front_image || l.images?.[0] || "";
const NL_MONTHS = ["Januari", "Februari", "Maart", "April", "Mei", "Juni", "Juli", "Augustus", "September", "Oktober", "November", "December"];

function monthLabel(l) {
  const m = (l.date_iso || "").match(/^\d{4}-(\d{2})/);
  if (!m) return null;
  const idx = Number(m[1]) - 1;
  return idx >= 0 && idx < 12 ? NL_MONTHS[idx] : null;
}

function buildYearGroups(list) {
  const groups = [];
  const idx = {};
  for (const l of list) {
    const iso = (l.date_iso || "").trim();
    const y = /^\d{4}/.test(iso) ? iso.slice(0, 4) : "Zonder datum";
    if (idx[y] === undefined) {
      idx[y] = groups.length;
      groups.push([y, []]);
    }
    groups[idx[y]][1].push(l);
  }
  const i = groups.findIndex((g) => g[0] === "Zonder datum");
  if (i > -1) {
    const [g] = groups.splice(i, 1);
    groups.push(g);
  }
  return groups;
}

function buildConversations(letters) {
  const byId = {};
  letters.forEach((l) => { byId[l.id] = l; });
  const adj = {};
  letters.forEach((l) => { adj[l.id] = []; });
  letters.forEach((l) => {
    if (l.reply_to && byId[l.reply_to]) {
      adj[l.id].push(l.reply_to);
      adj[l.reply_to].push(l.id);
    }
  });
  const seen = new Set();
  const comps = [];
  for (const l of letters) {
    if (seen.has(l.id)) continue;
    const stack = [l.id];
    seen.add(l.id);
    const comp = [];
    while (stack.length) {
      const id = stack.pop();
      comp.push(byId[id]);
      for (const n of adj[id]) if (!seen.has(n)) { seen.add(n); stack.push(n); }
    }
    comps.push(comp);
  }
  const multi = comps.filter((c) => c.length > 1).map((c) => sortByDate(c, "asc"));
  multi.sort((a, b) => (dateKey(a[0]) ?? Infinity) - (dateKey(b[0]) ?? Infinity));
  return multi;
}

function dateKey(l) {
  const iso = (l.date_iso || "").trim();
  const m = iso.match(/^(\d{4})(?:-(\d{1,2}))?(?:-(\d{1,2}))?/);
  if (!m) return null;
  return Number(m[1]) * 10000 + Number(m[2] || 0) * 100 + Number(m[3] || 0);
}
const writerKey = (l) => (l.writer || "").trim() || (l.signature || "").trim() || "Onbekende schrijver";

function letterYear(l) {
  const iso = (l.date_iso || "").match(/^(\d{4})/);
  if (iso) return Number(iso[1]);
  const y = (l.year || "").match(/\d{4}/);
  return y ? Number(y[0]) : null;
}

function sortByDate(list, dir) {
  const withKey = list.map((l) => ({ l, k: dateKey(l) }));
  const dated = withKey.filter((x) => x.k !== null).sort((a, b) => (dir === "asc" ? a.k - b.k : b.k - a.k));
  const undated = withKey.filter((x) => x.k === null);
  return [...dated.map((x) => x.l), ...undated.map((x) => x.l)];
}

export default function History() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const detailCache = useRef(new Map());
  const [visibleCount, setVisibleCount] = useState(30);
  const [showQuotes, setShowQuotes] = useState(false);
  const [quoteGen, setQuoteGen] = useState({ busy: false, done: 0, total: 0 });
  const [dir, setDir] = useState("asc");
  const [groupByWriter, setGroupByWriter] = useState(false);
  const [view] = useState("timeline");
  const [fWriter, setFWriter] = useState("all");
  const [fLang, setFLang] = useState("all");
  const [fPlace, setFPlace] = useState("all");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [onlyUnreadable, setOnlyUnreadable] = useState(false);
  const [story, setStory] = useState(null);
  const [storyLoading, setStoryLoading] = useState(false);
  const [storyGenerating, setStoryGenerating] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState({ done: 0, total: 0 });
  const [bookChapters, setBookChapters] = useState({});
  const [chapterTitleBusy, setChapterTitleBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      let data = null;
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          data = await api.listLetters(isAdmin ? { thumbnails: true } : {});
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          const status = e?.response?.status;
          if (status && status < 500 && status !== 408 && status !== 429) break;
          await new Promise((r) => setTimeout(r, 1500));
        }
      }
      if (lastErr) throw lastErr;
      setLetters(data);
    } catch (e) {
      toast.error("Kon Brievenboek niet laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    api.getBookChapters().then((c) => setBookChapters(c || {})).catch(() => {});
  }, []);

  // Hoofdstukken (jaar-maand) die nog geen boektitel hebben.
  const missingChapters = useMemo(() => {
    const seen = new Map();
    for (const l of letters) {
      const iso = (l.date_iso || "").slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(iso) || iso.endsWith("-00")) continue;
      if (bookChapters[iso]) continue;
      if (!seen.has(iso)) seen.set(iso, { year: iso.slice(0, 4), month: iso.slice(5, 7) });
    }
    return Array.from(seen.values());
  }, [letters, bookChapters]);

  const generateChapterTitles = async () => {
    if (!missingChapters.length) { toast.info("Alle hoofdstukken hebben al een titel"); return; }
    setChapterTitleBusy(true);
    try {
      const titles = await api.generateChapterTitles(missingChapters);
      setBookChapters((prev) => ({ ...prev, ...titles }));
      toast.success("Hoofdstuktitels gegenereerd");
    } catch (e) {
      toast.error("Titels genereren mislukt");
    } finally {
      setChapterTitleBusy(false);
    }
  };

  const loadStory = async () => {
    setStoryLoading(true);
    try {
      let data = null;
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          data = await api.getStory();
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          const status = e?.response?.status;
          // Alleen opnieuw proberen bij tijdelijke fouten (deploy/herstart), niet bij 4xx.
          if (status && status < 500 && status !== 408 && status !== 429) break;
          await new Promise((r) => setTimeout(r, 1500));
        }
      }
      if (lastErr) throw lastErr;
      setStory(data);
    } catch (e) {
      toast.error("Kon de momenten niet laden");
    } finally {
      setStoryLoading(false);
    }
  };
  useEffect(() => {
    if (view === "momenten" && !story) loadStory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const pendingHighlights = useMemo(
    () => letters.filter((l) => !(l.highlights?.length) && (l.transcription || "").trim()),
    [letters]
  );

  const updateStory = async () => {
    const prevUpdatedAt = story?.narrative_updated_at || "";
    // Haal eerst de actuele brievenlijst op, zodat ook net toegevoegde brieven (incl. de laatste) meetellen.
    let current = letters;
    try {
      current = await api.listLetters(isAdmin ? { thumbnails: true } : {});
      setLetters(current);
    } catch (e) {
      /* val terug op de bestaande lijst */
    }
    const pending = current.filter((l) => !(l.highlights?.length) && (l.transcription || "").trim());
    if (pending.length) {
      setBackfilling(true);
      setBackfillProgress({ done: 0, total: pending.length });
      const updated = {};
      for (let i = 0; i < pending.length; i++) {
        try {
          const res = await api.letters.generateHighlights(pending[i].id);
          updated[res.id] = res;
        } catch (e) {
          /* sla deze brief over en ga door */
        }
        setBackfillProgress({ done: i + 1, total: pending.length });
      }
      setLetters((prev) => prev.map((l) => updated[l.id] || l));
      setBackfilling(false);
    }
    // Start de generatie op de achtergrond en pol tot die klaar is (voorkomt ingress-timeout).
    setStoryGenerating(true);
    try {
      await api.generateStory();
      let done = false;
      for (let i = 0; i < 90 && !done; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        let data = null;
        try {
          data = await api.getStory();
        } catch (e) {
          continue; // tijdelijke fout, blijf pollen
        }
        setStory(data);
        if (!data.generating) {
          done = true;
          if (data.gen_error) {
            toast.error("Verhaal genereren mislukt: " + data.gen_error);
          } else if (data.narrative_updated_at && data.narrative_updated_at !== prevUpdatedAt) {
            toast.success("Verhaal bijgewerkt met alle brieven");
          } else {
            toast.success("Verhaal is bijgewerkt");
          }
        }
      }
      if (!done) {
        toast.message("Het verhaal wordt nog geschreven. Ververs de pagina over een minuut om het resultaat te zien.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verhaal genereren kon niet worden gestart");
    } finally {
      setStoryGenerating(false);
    }
  };
  const backfillHighlights = updateStory;

  const quotesMissing = useMemo(
    () => letters.filter((l) => !(l.pull_quote || "").trim() && (l.transcription || "").trim()),
    [letters]
  );

  const generateQuotes = async () => {
    const pending = quotesMissing;
    if (!pending.length) return;
    setQuoteGen({ busy: true, done: 0, total: pending.length });
    try {
      const res = await api.letters.generateQuotesBatch(pending.map((l) => l.id));
      setLetters((prev) => prev.map((l) => (res[l.id] ? { ...l, pull_quote: res[l.id].pull_quote, pull_quote_by: res[l.id].pull_quote_by } : l)));
      toast.success("Quotes gegenereerd");
    } catch (e) {
      toast.error("Quotes genereren mislukt");
    } finally {
      setQuoteGen({ busy: false, done: 0, total: 0 });
    }
  };

  const writerOptions = useMemo(() => [...new Set(letters.map(writerKey))].sort(), [letters]);
  const langOptions = useMemo(() => [...new Set(letters.map((l) => l.source_language).filter(Boolean))].sort(), [letters]);
  const placeOptions = useMemo(() => [...new Set(letters.map((l) => l.place).filter(Boolean))].sort(), [letters]);

  const filtered = useMemo(() => {
    const term = q.toLowerCase().trim();
    const yf = yearFrom ? Number(yearFrom) : null;
    const yt = yearTo ? Number(yearTo) : null;
    return letters.filter((l) => {
      if (term && ![l.title, l.region, l.year, l.transcription, l.writer, l.place, l.signature, l.salutation, l.summary]
        .some((f) => (f || "").toLowerCase().includes(term))) return false;
      if (fWriter !== "all" && writerKey(l) !== fWriter) return false;
      if (fLang !== "all" && l.source_language !== fLang) return false;
      if (fPlace !== "all" && l.place !== fPlace) return false;
      if (yf || yt) {
        const y = letterYear(l);
        if (y === null) return false;
        if (yf && y < yf) return false;
        if (yt && y > yt) return false;
      }
      if (onlyUnreadable && countUnreadable(l.transcription) === 0) return false;
      return true;
    });
  }, [letters, q, fWriter, fLang, fPlace, yearFrom, yearTo, onlyUnreadable]);

  const activeFilterCount = (fWriter !== "all" ? 1 : 0) + (fLang !== "all" ? 1 : 0) + (fPlace !== "all" ? 1 : 0) + (yearFrom ? 1 : 0) + (yearTo ? 1 : 0) + (onlyUnreadable ? 1 : 0);
  const clearFilters = () => { setFWriter("all"); setFLang("all"); setFPlace("all"); setYearFrom(""); setYearTo(""); setQ(""); setOnlyUnreadable(false); };

  const sorted = useMemo(() => sortByDate(filtered, dir), [filtered, dir]);
  const searchTerm = q.trim().toLowerCase();

  const visibleSorted = useMemo(() => sorted.slice(0, visibleCount), [sorted, visibleCount]);
  const hasMore = visibleCount < sorted.length;

  useEffect(() => { setVisibleCount(30); }, [view, dir, fWriter, fLang, fPlace, yearFrom, yearTo, q, onlyUnreadable, groupByWriter]);

  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setVisibleCount((c) => c + 30);
    }, { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, visibleCount]);

  const groups = useMemo(() => {
    if (!groupByWriter) return null;
    const map = new Map();
    for (const l of sorted) {
      const w = writerKey(l);
      if (!map.has(w)) map.set(w, []);
      map.get(w).push(l);
    }
    return [...map.entries()].sort((a, b) => {
      if (a[0] === "Onbekende schrijver") return 1;
      if (b[0] === "Onbekende schrijver") return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [sorted, groupByWriter]);

  const conversations = useMemo(() => buildConversations(letters), [letters]);

  const handleLink = async (id, replyTo) => {
    try {
      const updated = await api.linkLetter(id, replyTo);
      setLetters((prev) => prev.map((l) => (l.id === id ? updated : l)));
      setActive((a) => (a && a.id === id ? updated : a));
      toast.success(replyTo ? "Gekoppeld als antwoord" : "Koppeling verwijderd");
    } catch (e) {
      toast.error("Koppelen mislukt");
    }
  };

  const handleRotateFront = async (id) => {
    const l = (letters || []).find((x) => x.id === id) || active;
    if (!l?.front_image) return;
    try {
      const rotated = await rotateDataUrl(l.front_image, 90);
      const updated = await api.updateLetterFront(id, rotated);
      setLetters((prev) => prev.map((x) => (x.id === id ? updated : x)));
      setActive((a) => (a && a.id === id ? updated : a));
      toast.success("Voorkant gedraaid");
    } catch (e) {
      toast.error("Draaien mislukt");
    }
  };

  const handleSaveMeta = async (id, meta) => {
    try {
      const updated = await api.updateLetterMeta(id, meta);
      setLetters((prev) => prev.map((l) => (l.id === id ? updated : l)));
      setActive((a) => (a && a.id === id ? updated : a));
      toast.success("Gegevens bijgewerkt");
    } catch (e) {
      toast.error("Bijwerken mislukt");
    }
  };

  const handleSetFront = async (id, dataUrl) => {
    try {
      const updated = await api.updateLetterFront(id, dataUrl);
      setLetters((prev) => prev.map((x) => (x.id === id ? updated : x)));
      setActive((a) => (a && a.id === id ? updated : a));
      toast.success(dataUrl ? "Voorkant bijgewerkt" : "Voorkant verwijderd");
    } catch (e) {
      toast.error("Voorkant bijwerken mislukt");
    }
  };

  const handleRotateImage = async (id, idx) => {
    const l = (letters || []).find((x) => x.id === id) || active;
    if (!l?.images?.[idx]) return;
    try {
      const rotated = await rotateDataUrl(l.images[idx], 90);
      const newImages = l.images.map((im, i) => (i === idx ? rotated : im));
      const updated = await api.updateLetterImages(id, newImages);
      setLetters((prev) => prev.map((x) => (x.id === id ? updated : x)));
      setActive((a) => (a && a.id === id ? updated : a));
      toast.success("Scan gedraaid");
    } catch (e) {
      toast.error("Draaien mislukt");
    }
  };

  const handleRename = async (id, title) => {
    try {
      const updated = await api.updateLetterTitle(id, title);
      setLetters((prev) => prev.map((l) => (l.id === id ? updated : l)));
      setActive((a) => (a && a.id === id ? updated : a));
      toast.success("Titel bijgewerkt");
    } catch (e) {
      toast.error("Titel bijwerken mislukt");
    }
  };

  const handleLetterUpdated = (updated) => {
    setLetters((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setActive((a) => (a && a.id === updated.id ? updated : a));
  };

  const remove = async (id) => {
    if (typeof window !== "undefined" && !window.confirm("Weet je zeker dat je deze brief definitief wilt verwijderen? Dit kan niet ongedaan worden gemaakt.")) return;
    try {
      await api.deleteLetter(id);
      setLetters((prev) => prev.filter((l) => l.id !== id));
      if (active?.id === id) setActive(null);
      toast.success("Verwijderd");
    } catch (e) {
      toast.error("Verwijderen mislukt");
    }
  };

  const fetchDetail = (id) => {
    if (detailCache.current.has(id)) return Promise.resolve(detailCache.current.get(id));
    return api.getLetter(id).then((full) => { detailCache.current.set(id, full); return full; });
  };

  const prefetchNeighbors = (id) => {
    const idx = sorted.findIndex((x) => x.id === id);
    if (idx === -1) return;
    [sorted[idx + 1], sorted[idx - 1]].forEach((n) => {
      if (n && !detailCache.current.has(n.id)) fetchDetail(n.id).catch(() => {});
    });
  };

  const openLetter = (l) => {
    if (!l) return;
    // Direct tonen vanuit cache als de volledige brief al is opgehaald; anders de lichte versie tonen.
    const cached = detailCache.current.get(l.id);
    setActive(cached || l);
    if (!cached) {
      fetchDetail(l.id)
        .then((full) => setActive((a) => (a && a.id === l.id ? full : a)))
        .catch(() => { /* lijstversie blijft staan */ });
    }
    prefetchNeighbors(l.id);
  };

  // Blader-navigatie binnen de huidige (gesorteerde) reeks.
  const navList = sorted.length ? sorted : sortByDate(letters, dir);
  const activeIdx = active ? navList.findIndex((x) => x.id === active.id) : -1;
  const hasPrev = activeIdx > 0;
  const hasNext = activeIdx !== -1 && activeIdx < navList.length - 1;
  const goPrev = () => { if (hasPrev) openLetter(navList[activeIdx - 1]); };
  const goNext = () => { if (hasNext) openLetter(navList[activeIdx + 1]); };

  const copy = (t) => {
    navigator.clipboard.writeText(stripMarkers(t));
    toast.success("Gekopieerd");
  };
  const download = (l) => {
    const blob = new Blob([stripMarkers(l.transcription)], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${l.title || "transcriptie"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="mx-auto max-w-[1500px] px-4 py-6 sm:px-8">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 font-serif text-xl font-bold tracking-tight text-[var(--ink)]"><GalleryVerticalEnd className="h-5 w-5 text-[var(--brand)]" /> Tijdlijn &amp; Correcties</h2>
          <p className="mt-1 text-sm text-[var(--ink-2)]">Alle brieven chronologisch. Kom je een <span className="font-semibold text-[#dc2626]">rood gemarkeerd</span> woord tegen? Klik de brief en help ons lezen wat er staat.</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--ink-3)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Doorzoek alle tekst: naam, plaats, gebeurtenis…"
            data-testid="history-search"
            className="h-10 w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] pl-9 pr-3 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
          />
          {q.trim() && (
            <span className="mt-1 block text-right font-mono text-[11px] text-[var(--ink-3)]" data-testid="search-result-count">
              {filtered.length} resulta{filtered.length === 1 ? "at" : "ten"}
            </span>
          )}
        </div>
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {isAdmin && (
          <button
            onClick={() => setShowQuotes((v) => !v)}
            data-testid="toggle-quotes"
            aria-pressed={showQuotes}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
              showQuotes
                ? "border-[var(--brand)] bg-[var(--brand)]/10 text-[var(--brand)]"
                : "border-[var(--border-subtle)] text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"
            }`}
          >
            <Quote className="h-4 w-4" /> Quotes uitlichten
          </button>
        )}
        {isAdmin && showQuotes && quotesMissing.length > 0 && (
          <button
            onClick={generateQuotes}
            disabled={quoteGen.busy}
            data-testid="generate-quotes"
            className="flex items-center gap-2 rounded-lg border border-[var(--brand)]/40 bg-[var(--paper)] px-3 py-2 text-sm font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--brand)]/10 disabled:opacity-60"
          >
            {quoteGen.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {quoteGen.busy ? "Quotes genereren…" : `Genereer ${quotesMissing.length} quote${quotesMissing.length === 1 ? "" : "s"}`}
          </button>
        )}
        {view === "grid" && (
          <div className="flex overflow-hidden rounded-lg border border-[var(--border-subtle)]">
            <SegBtn active={!groupByWriter} onClick={() => setGroupByWriter(false)} testid="group-all">
              <Users className="h-4 w-4" /> Alles door elkaar
            </SegBtn>
            <SegBtn active={groupByWriter} onClick={() => setGroupByWriter(true)} testid="group-writer">
              <User className="h-4 w-4" /> Per schrijver
            </SegBtn>
          </div>
        )}
        {view !== "momenten" && (
          <>
            <button
              onClick={() => setDir((d) => (d === "asc" ? "desc" : "asc"))}
              data-testid="sort-direction"
              className="flex items-center gap-2 rounded-lg border border-[var(--border-subtle)] px-3 py-2 text-sm font-medium text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)]"
            >
              {dir === "asc" ? <ArrowUpNarrowWide className="h-4 w-4" /> : <ArrowDownNarrowWide className="h-4 w-4" />}
              {dir === "asc" ? "Oud → nieuw" : "Nieuw → oud"}
            </button>
            <button
              onClick={() => setOnlyUnreadable((v) => !v)}
              data-testid="filter-unreadable"
              aria-pressed={onlyUnreadable}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                onlyUnreadable
                  ? "border-[var(--amber-border)] bg-[var(--amber-bg)] text-[var(--amber-text)]"
                  : "border-[var(--border-subtle)] text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"
              }`}
            >
              <Lightbulb className="h-4 w-4" /> Onleesbare tekst
            </button>
          </>
        )}
      </div>

      {isAdmin && !loading && letters.length > 0 && (
        <div className="mb-5 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface)] p-3" data-testid="filter-bar">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">
              <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
            </span>
            <FilterSelect value={fWriter} onChange={setFWriter} placeholder="Schrijver" options={writerOptions} testid="filter-writer" allLabel="Alle schrijvers" />
            <FilterSelect value={fLang} onChange={setFLang} placeholder="Taal" options={langOptions} testid="filter-language" allLabel="Alle talen" />
            <FilterSelect value={fPlace} onChange={setFPlace} placeholder="Plaats" options={placeOptions} testid="filter-place" allLabel="Alle plaatsen" />
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                value={yearFrom}
                onChange={(e) => setYearFrom(e.target.value)}
                placeholder="van jaar"
                data-testid="filter-year-from"
                className="h-9 w-24 rounded-lg border border-[var(--border-subtle)] bg-[var(--paper)] px-2.5 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
              />
              <span className="text-[var(--ink-3)]">–</span>
              <input
                type="number"
                value={yearTo}
                onChange={(e) => setYearTo(e.target.value)}
                placeholder="tot jaar"
                data-testid="filter-year-to"
                className="h-9 w-24 rounded-lg border border-[var(--border-subtle)] bg-[var(--paper)] px-2.5 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
              />
            </div>
            <div className="flex-1" />
            <span className="font-mono text-[11px] text-[var(--ink-3)]" data-testid="filter-result-count">{filtered.length} van {letters.length}</span>
            {(activeFilterCount > 0 || q) && (
              <button
                onClick={clearFilters}
                data-testid="clear-filters"
                className="flex items-center gap-1.5 rounded-lg border border-[var(--border-subtle)] px-3 py-1.5 text-xs font-medium text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)]"
              >
                <X className="h-3.5 w-3.5" /> Wis filters
              </button>
            )}
          </div>
        </div>
      )}

      {view === "momenten" ? (
        <Moments
          story={story}
          loading={storyLoading}
          generating={storyGenerating}
          isAdmin={isAdmin}
          onRegenerate={updateStory}
          onOpenLetter={(id) => { const l = letters.find((x) => x.id === id); if (l) openLetter(l); }}
          onBackfill={updateStory}
          backfilling={backfilling}
          backfillProgress={backfillProgress}
          pendingCount={pendingHighlights.length}
          letters={letters}
          showQuotes={showQuotes}
          onGenerateChapterTitles={generateChapterTitles}
          chapterTitleBusy={chapterTitleBusy}
          missingChapterCount={missingChapters.length}
        />
      ) : loading ? (
        <div className="flex items-center justify-center py-24 text-[var(--ink-3)]">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : sorted.length === 0 ? (
        <Empty />
      ) : view === "conversations" ? (
        <Conversations convos={conversations} onOpen={openLetter} />
      ) : view === "timeline" ? (
        <Timeline items={sorted} onOpen={openLetter} onRemove={isAdmin ? remove : null} term={searchTerm} showQuotes={isAdmin && showQuotes} />
      ) : groupByWriter ? (
        <div className="space-y-8">
          {groups.map(([writer, items]) => (
            <section key={writer} data-testid={`writer-group-${writer}`}>
              <div className="mb-3 flex items-center gap-2 border-b border-[var(--border-subtle)] pb-2">
                <User className="h-4 w-4 text-[var(--brand)]" />
                <h2 className="font-serif text-xl font-semibold text-[var(--ink)]">{writer}</h2>
                <span className="font-mono text-xs text-[var(--ink-3)]">{items.length} {items.length === 1 ? "brief" : "brieven"}</span>
              </div>
              <Grid items={items} onOpen={openLetter} onCopy={copy} onDownload={download} onRemove={isAdmin ? remove : null} term={searchTerm} />
            </section>
          ))}
        </div>
      ) : (
        <>
          <Grid items={visibleSorted} onOpen={openLetter} onCopy={copy} onDownload={download} onRemove={isAdmin ? remove : null} term={searchTerm} />
          {hasMore && <div ref={sentinelRef} data-testid="load-more-sentinel" className="h-10" />}
        </>
      )}

      <DetailDialog active={active} setActive={setActive} copy={copy} download={download} letters={letters} isAdmin={isAdmin} onLink={handleLink} onRename={handleRename} onRotateFront={handleRotateFront} onRotateImage={handleRotateImage} onSaveMeta={handleSaveMeta} onSetFront={handleSetFront} onReload={load} onLetterUpdated={handleLetterUpdated} onDelete={remove} onPrev={goPrev} onNext={goNext} hasPrev={hasPrev} hasNext={hasNext} position={activeIdx + 1} total={navList.length} />
      <ScrollToTop />
    </main>
  );
}

function momentYear(m) {
  const iso = (m.date_iso || "").match(/^(\d{4})/);
  return iso ? iso[1] : "Zonder datum";
}

function buildMomentYears(moments) {
  const groups = [];
  const idx = {};
  for (const m of moments) {
    const y = momentYear(m);
    if (idx[y] === undefined) { idx[y] = groups.length; groups.push([y, []]); }
    groups[idx[y]][1].push(m);
  }
  const i = groups.findIndex((g) => g[0] === "Zonder datum");
  if (i > -1) { const [g] = groups.splice(i, 1); groups.push(g); }
  return groups;
}

function Moments({ story, loading, generating, isAdmin, onRegenerate, onOpenLetter, onBackfill, backfilling, backfillProgress, pendingCount, letters, showQuotes, onGenerateChapterTitles, chapterTitleBusy, missingChapterCount }) {
  const navigate = useNavigate();
  const [storyExpanded, setStoryExpanded] = useState(false);
  const BackfillButton = ({ full }) => (
    <button
      onClick={onBackfill}
      disabled={backfilling}
      data-testid="moments-backfill"
      className={`flex items-center justify-center gap-2 rounded-lg bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60 ${full ? "w-full sm:w-auto" : ""}`}
    >
      {backfilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
      {backfilling
        ? `Bezig… ${backfillProgress?.done || 0}/${backfillProgress?.total || 0} brieven`
        : `Genereer verhaal uit ${pendingCount} ${pendingCount === 1 ? "brief" : "brieven"}`}
    </button>
  );

  const moments = story?.moments || [];
  const timelineItems = sortByDate(letters || [], "asc");
  if (!timelineItems.length) {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-24 text-[var(--ink-3)]" data-testid="moments-loading">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="mt-3 text-sm">Het verhaal laden…</p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border-active)] py-20 text-center" data-testid="moments-empty">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--surface-muted)] text-[var(--ink-3)]">
          <Heart className="h-6 w-6" />
        </div>
        <p className="mt-4 font-display font-semibold text-[var(--ink)]">Nog geen brieven</p>
        <p className="mt-1 max-w-sm text-sm text-[var(--ink-3)]">Zodra er brieven in het Brievenboek staan, verschijnt hier het verhaal van Pier &amp; Ien met een tijdlijn van alle brieven.</p>
      </div>
    );
  }
  return (
    <div className="space-y-8" data-testid="moments">
      {isAdmin && pendingCount > 0 && (
        <div className="flex flex-col gap-3 rounded-xl border border-[var(--amber-border)]/60 bg-[var(--amber-bg)] p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="moments-backfill-bar">
          <p className="flex items-start gap-2 text-sm text-[var(--amber-text)]">
            <Lightbulb className="mt-0.5 h-4 w-4 shrink-0" />
            {pendingCount} {pendingCount === 1 ? "brief doet" : "brieven doen"} nog niet mee in het verhaal. Klik om hun hoogtepunten op te halen en het verhaal bij te werken.
          </p>
          <BackfillButton />
        </div>
      )}
      <section className="relative overflow-hidden rounded-2xl border border-[var(--brand)]/25 bg-gradient-to-br from-[var(--brand)]/8 via-[var(--surface)] to-[var(--surface)] p-6 shadow-sm sm:p-8" data-testid="moments-narrative">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--brand)]/15 text-[var(--brand)]">
              <Heart className="h-4.5 w-4.5" />
            </span>
            <h2 className="font-serif text-2xl font-bold tracking-tight text-[var(--ink)]">Verhaal van Pier &amp; Ien</h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {story?.narrative && (
              <button
                onClick={() => navigate("/verhaal-boek")}
                data-testid="moments-print"
                className="flex items-center gap-1.5 rounded-lg border border-[var(--brand)]/40 bg-[var(--paper)] px-3 py-1.5 text-xs font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--brand)]/10"
              >
                <Printer className="h-3.5 w-3.5" /> Afdrukken / bewaren
              </button>
            )}
            {isAdmin && missingChapterCount > 0 && (
              <button
                onClick={onGenerateChapterTitles}
                disabled={chapterTitleBusy}
                data-testid="moments-generate-chapter-titles"
                className="flex items-center gap-1.5 rounded-lg border border-[var(--brand)]/40 bg-[var(--paper)] px-3 py-1.5 text-xs font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--brand)]/10 disabled:opacity-50"
              >
                {chapterTitleBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BookOpen className="h-3.5 w-3.5" />}
                {chapterTitleBusy ? "Titels genereren…" : `Hoofdstuktitels (${missingChapterCount})`}
              </button>
            )}
            {isAdmin && timelineItems.length > 0 && (
              <button
                onClick={onRegenerate}
                disabled={generating || backfilling}
                data-testid="moments-regenerate"
                className="flex items-center gap-1.5 rounded-lg border border-[var(--brand)]/40 bg-[var(--paper)] px-3 py-1.5 text-xs font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--brand)]/10 disabled:opacity-50"
              >
                {(generating || backfilling) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {backfilling
                  ? `Bijwerken… ${backfillProgress?.done || 0}/${backfillProgress?.total || 0}`
                  : generating
                    ? "Verhaal wordt geschreven…"
                    : story?.narrative
                      ? "Verhaal bijwerken"
                      : "Genereer verhaal"}
              </button>
            )}
          </div>
        </div>
        {story?.narrative ? (() => {
          const paras = story.narrative.split(/\n{2,}|\n/).map((p) => p.trim()).filter(Boolean);
          const collapsible = paras.length > 2;
          const expanded = storyExpanded || !collapsible;
          const visible = expanded ? paras : paras.slice(0, 2);

          // Een handjevol decoratieve quotes in het verhaal — meer naarmate er meer brieven zijn.
          const cand = (letters || [])
            .filter((l) => (l.pull_quote || "").trim())
            .slice()
            .sort((a, b) => (a.date_iso || "").localeCompare(b.date_iso || ""))
            .map((l) => ({ q: l.pull_quote.trim(), by: l.pull_quote_by || l.writer || "" }));
          const picks = [];
          if (showQuotes && expanded && cand.length) {
            const target = Math.min(cand.length, 8, Math.max(1, Math.round((letters?.length || 0) / 10)));
            const step = cand.length / target;
            for (let k = 0; k < target; k++) picks.push(cand[Math.floor(k * step)]);
          }
          const proseCount = visible.filter((p) => !/^#{1,3}\s/.test(p)).length;
          const gap = picks.length ? Math.max(2, Math.ceil(proseCount / (picks.length + 1))) : 0;

          const els = [];
          let proseSeen = 0;
          let qi = 0;
          visible.forEach((p, i) => {
            if (/^#{1,3}\s/.test(p)) {
              els.push(
                <h3 key={`b-${i}`} data-testid="story-chapter-heading" className={`font-serif text-xl font-bold text-[var(--brand)] ${i === 0 ? "" : "mt-6"} border-b border-[var(--brand)]/20 pb-1`}>
                  {p.replace(/^#{1,3}\s+/, "")}
                </h3>
              );
              return;
            }
            els.push(<p key={`b-${i}`} className="font-serif text-lg leading-relaxed text-[var(--ink)]">{p}</p>);
            proseSeen++;
            if (gap && qi < picks.length && proseSeen % gap === 0) {
              const pk = picks[qi++];
              els.push(
                <figure key={`q-${i}-${qi}`} data-testid="story-inline-quote" className="my-5 border-l-4 border-[var(--brand)] bg-[var(--brand)]/5 py-3 pl-4 pr-3">
                  <p className="font-serif text-xl italic leading-snug text-[var(--ink)]">“{pk.q}”</p>
                  {pk.by && <figcaption className="mt-1 font-mono text-[10px] uppercase tracking-wider text-[var(--brand)]">— {pk.by}</figcaption>}
                </figure>
              );
            }
          });
          return (
            <div className="max-w-3xl" data-testid="moments-narrative-text">
              <div className={`relative space-y-3 ${collapsible && !storyExpanded ? "max-h-[11rem] overflow-hidden" : ""}`}>
                {els}
                {collapsible && !storyExpanded && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[var(--surface)] to-transparent" />
                )}
              </div>
              {collapsible && (
                <button
                  onClick={() => setStoryExpanded((v) => !v)}
                  data-testid="moments-narrative-toggle"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-[var(--brand)]/40 bg-[var(--paper)] px-3 py-1.5 text-sm font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--brand)]/10"
                >
                  {storyExpanded ? <><ChevronUp className="h-4 w-4" /> Minder</> : <><ChevronDown className="h-4 w-4" /> Meer lezen</>}
                </button>
              )}
            </div>
          );
        })() : (generating || loading) ? (
          <p className="flex max-w-3xl items-center gap-2 text-sm italic text-[var(--ink-3)]" data-testid="moments-narrative-empty">
            <Loader2 className="h-4 w-4 animate-spin" /> Het liefdesverhaal wordt geschreven…
          </p>
        ) : (
          <p className="max-w-3xl text-sm italic text-[var(--ink-3)]" data-testid="moments-narrative-empty">
            {isAdmin
              ? (pendingCount > 0
                  ? "Klik op \u201CGenereer verhaal uit brieven\u201D hierboven; de AI haalt de hoogtepunten uit elke brief, maakt er een titel bij en schrijft daar het liefdesverhaal van."
                  : "Klik op \u201CGenereer verhaal\u201D om van alle hoogtepunten een vloeiend liefdesverhaal te maken.")
              : "Het verhaal wordt binnenkort toegevoegd."}
          </p>
        )}
        {story?.narrative && (
          <p className="mt-4 font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">{moments.length} momenten uit de brieven</p>
        )}
      </section>

      <div data-testid="moments-timeline">
        <Timeline items={timelineItems} onOpen={(l) => onOpenLetter(l.id)} onRemove={null} term="" showQuotes={showQuotes} />
      </div>
    </div>
  );
}

function Conversations({ convos, onOpen }) {
  if (!convos.length) {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border-active)] py-20 text-center" data-testid="conversations-empty">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--surface-muted)] text-[var(--ink-3)]">
          <MessagesSquare className="h-6 w-6" />
        </div>
        <p className="mt-4 font-display font-semibold text-[var(--ink)]">Nog geen gekoppelde brieven</p>
        <p className="mt-1 max-w-sm text-sm text-[var(--ink-3)]">Open een brief en kies bij "Antwoord op" de brief waarop deze reageert. Gekoppelde brieven verschijnen hier als gesprek.</p>
      </div>
    );
  }
  return (
    <div className="space-y-6" data-testid="conversations">
      {convos.map((thread, ti) => {
        const sides = {};
        let si = 0;
        const sideFor = (l) => {
          const w = writerKey(l);
          if (!(w in sides)) { sides[w] = si % 2 === 0 ? "left" : "right"; si += 1; }
          return sides[w];
        };
        return (
          <section key={ti} data-testid={`conversation-${ti}`} className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface)] p-4 shadow-sm">
            <div className="mb-3 flex items-center gap-2 border-b border-[var(--border-subtle)] pb-2">
              <MessagesSquare className="h-4 w-4 text-[var(--brand)]" />
              <h2 className="font-serif text-lg font-semibold text-[var(--ink)]">Briefwisseling</h2>
              <span className="font-mono text-xs text-[var(--ink-3)]">{thread.length} brieven</span>
            </div>
            <div className="space-y-3">
              {thread.map((l) => {
                const side = sideFor(l);
                return (
                  <div key={l.id} className={`flex ${side === "right" ? "justify-end" : "justify-start"}`}>
                    <button
                      onClick={() => onOpen(l)}
                      data-testid={`conversation-letter-${l.id}`}
                      className={`max-w-[80%] rounded-2xl border p-3 text-left transition-colors hover:border-[var(--brand)] ${
                        side === "right"
                          ? "rounded-tr-sm border-[var(--brand)]/30 bg-[var(--brand)]/5"
                          : "rounded-tl-sm border-[var(--border-subtle)] bg-[var(--surface-muted)]"
                      }`}
                    >
                      <div className="flex items-center gap-2 font-mono text-[11px] text-[var(--ink-3)]">
                        <User className="h-3 w-3" /> {l.writer || writerKey(l)}
                        <span>·</span>
                        <Calendar className="h-3 w-3" /> {l.date_text || l.date_iso || l.year || "—"}
                      </div>
                      <h3 className="mt-0.5 font-serif text-base font-semibold text-[var(--ink)]">{l.title}</h3>
                      <p className="mt-1 text-sm text-[var(--ink-2)] line-clamp-2">{l.summary || stripMarkers(l.transcription)}</p>
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function groupByMonth(letters) {
  const groups = [];
  const idx = {};
  for (const l of letters) {
    const label = monthLabel(l);
    const key = label || "__none__";
    if (idx[key] === undefined) {
      idx[key] = groups.length;
      groups.push([label, []]);
    }
    groups[idx[key]][1].push(l);
  }
  return groups;
}

function writerCounts(letters) {
  const counts = {};
  for (const l of letters) {
    const w = writerKey(l);
    counts[w] = (counts[w] || 0) + 1;
  }
  return Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

function Timeline({ items, onOpen, onRemove, term, showQuotes }) {
  const years = buildYearGroups(items);
  return (
    <div className="relative pl-10" data-testid="timeline">
      <div className="absolute bottom-2 left-[14px] top-2 w-px bg-[var(--border-active)]" />
      {years.map(([year, letters]) => (
        <section key={year} className="mb-8" data-testid={`timeline-year-${year}`}>
          <div className="relative mb-4">
            <span className="absolute -left-[30px] top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-[var(--brand)] bg-[var(--paper)]">
              <span className="h-2 w-2 rounded-full bg-[var(--brand)]" />
            </span>
            <h2 className="font-serif text-2xl font-bold text-[var(--ink)]">{year}</h2>
          </div>
          {groupByMonth(letters).map(([month, mLetters]) => (
            <div key={month || "__none__"} className="mb-5">
              {month && (
                <div className="mb-2 ml-1 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid={`timeline-month-${year}-${month}`}>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--ink-3)]">{month}</p>
                  <span className="text-[var(--ink-3)]">·</span>
                  {writerCounts(mLetters).map(([w, c]) => (
                    <span
                      key={w}
                      data-testid={`timeline-writer-count-${year}-${month}-${w}`}
                      className="inline-flex items-center gap-1 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-2 py-0.5 font-mono text-[10px] text-[var(--ink-2)]"
                      title={`${w}: ${c} ${c === 1 ? "brief" : "brieven"} in ${month} ${year}`}
                    >
                      <User className="h-2.5 w-2.5" />{w}: {c}
                    </span>
                  ))}
                </div>
              )}
              <div className="space-y-3">
                {mLetters.map((l) => (
                  <article
                    key={l.id}
                    data-testid={`timeline-card-${l.id}`}
                    onClick={() => onOpen(l)}
                    className="group relative flex cursor-pointer gap-3 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface)] p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[var(--brand)] hover:shadow-md"
                  >
                    <span className="absolute -left-[26px] top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full border-2 border-[var(--surface)] bg-[var(--border-active)] group-hover:bg-[var(--brand)]" />
                    {coverOf(l) && (
                      <img src={coverOf(l)} alt={l.title} className="h-16 w-16 shrink-0 rounded-md object-cover" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[11px] text-[var(--ink-3)]">
                        <Calendar className="h-3 w-3" /> {l.date_text || l.date_iso || "—"}
                        {l.place && <><span>·</span><MapPin className="h-3 w-3" /> {l.place}</>}
                        <span>·</span><DocBadge l={l} />
                        {countUnreadable(l.transcription) > 0 && <UnreadableBadge count={countUnreadable(l.transcription)} />}
                      </div>
                      {l.date_note && <p className="text-[11px] italic text-[var(--ink-3)]">{l.date_note}</p>}
                      {l.date_user_note && <p className="text-[11px] italic text-[var(--brand)]"><PenLine className="mr-1 inline h-3 w-3" />{l.date_user_note}</p>}
                      {(l.writer || l.signature) && (
                        <p className="font-mono text-[11px] text-[var(--ink-3)]"><User className="mr-1 inline h-3 w-3" />{l.writer || writerKey(l)}</p>
                      )}
                      <p className="mt-1 text-sm text-[var(--ink-2)]">{l.summary || stripMarkers(l.transcription)}</p>
                      {showQuotes && l.pull_quote && (
                        <blockquote data-testid={`timeline-quote-${l.id}`} className="my-2 border-l-[3px] border-[var(--brand)] bg-[var(--brand)]/5 py-1.5 pl-3 pr-2">
                          <span className="font-serif text-[15px] italic leading-snug text-[var(--ink)]">“{l.pull_quote}”</span>
                          <cite className="mt-1 block font-mono text-[10px] not-italic uppercase tracking-wider text-[var(--brand)]">— {l.pull_quote_by || l.writer || writerKey(l)}</cite>
                        </blockquote>
                      )}
                      {l.tone && <ToneTag tone={l.tone} testid={`timeline-tone-${l.id}`} />}
                      {l.highlights?.length > 0 && <HighlightTags items={l.highlights} className="mt-2" />}
                      <SearchSnippet letter={l} term={term} testid={`timeline-snippet-${l.id}`} />
                    </div>
                    {onRemove && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onRemove(l.id); }}
                        data-testid={`timeline-delete-${l.id}`}
                        className="h-8 w-8 shrink-0 self-start rounded-md text-[var(--ink-3)] opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                      >
                        <Trash2 className="mx-auto h-4 w-4" />
                      </button>
                    )}
                  </article>
                ))}
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function Grid({ items, onOpen, onCopy, onDownload, onRemove, term }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {items.map((l) => (
        <article
          key={l.id}
          data-testid={`history-card-${l.id}`}
          className="group flex flex-col overflow-hidden rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface)] shadow-sm transition-all hover:-translate-y-1 hover:shadow-md"
        >
          <button onClick={() => onOpen(l)} className="relative h-40 overflow-hidden bg-[var(--surface-muted)]">
            {coverOf(l) ? (
              <img src={coverOf(l)} alt={l.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            ) : (
              <div className="flex h-full items-center justify-center text-[var(--ink-3)]"><FileText className="h-8 w-8" /></div>
            )}
            {(l.date_text || l.date_iso) && (
              <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur">
                {l.date_text || l.date_iso}
              </span>
            )}
            {l.front_image && (
              <span className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur">voorkant</span>
            )}
            {countUnreadable(l.transcription) > 0 && (
              <UnreadableBadge count={countUnreadable(l.transcription)} className="absolute right-2 top-2 shadow-sm" />
            )}
          </button>
          <div className="flex flex-1 flex-col p-4">
            <button onClick={() => onOpen(l)} className="text-left">
              <h3 className="font-serif text-lg font-semibold leading-tight text-[var(--ink)] line-clamp-1">{l.title}</h3>
            </button>
            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11px] text-[var(--ink-3)]">
              <DocBadge l={l} />
              {(l.writer || l.signature) && <span className="flex items-center gap-1"><User className="h-3 w-3" />{l.writer || writerKey(l)}</span>}
              {l.place && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{l.place}</span>}
              {(l.date_text || l.date_iso) && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{l.date_text || l.date_iso}</span>}
              {!l.date_text && !l.date_iso && l.year && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{l.year}</span>}
            </div>
            {l.date_note && <p className="mt-1 text-[11px] italic text-[var(--ink-3)]">{l.date_note}</p>}
            {l.date_user_note && <p className="mt-1 text-[11px] italic text-[var(--brand)]"><PenLine className="mr-1 inline h-3 w-3" />{l.date_user_note}</p>}
            {l.salutation && <p className="mt-2 font-hand text-base leading-tight text-[var(--brand)] line-clamp-1">{l.salutation}</p>}
            {l.summary ? (
              <p className="mt-1 flex-1 text-sm italic leading-relaxed text-[var(--ink-2)]" data-testid={`card-summary-${l.id}`}>{l.summary}</p>
            ) : (
              <p className="mt-1 flex-1 text-sm leading-relaxed text-[var(--ink-2)] line-clamp-2">{stripMarkers(l.transcription)}</p>
            )}
            {l.tone && <ToneTag tone={l.tone} testid={`card-tone-${l.id}`} />}
            {l.highlights?.length > 0 && <HighlightTags items={l.highlights} className="mt-2" />}
            <SearchSnippet letter={l} term={term} testid={`card-snippet-${l.id}`} />
            <div className="mt-3 flex items-center gap-1 border-t border-[var(--border-subtle)] pt-3">
              <IconBtn onClick={() => onCopy(l.transcription)} testid={`history-copy-${l.id}`}><Copy className="h-4 w-4" /></IconBtn>
              <IconBtn onClick={() => onDownload(l)} testid={`history-download-${l.id}`}><Download className="h-4 w-4" /></IconBtn>
              <div className="flex-1" />
              {onRemove && <IconBtn onClick={() => onRemove(l.id)} danger testid={`history-delete-${l.id}`}><Trash2 className="h-4 w-4" /></IconBtn>}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}

function DetailDialog({ active, setActive, copy, download, letters, isAdmin, onLink, onRename, onRotateFront, onRotateImage, onSaveMeta, onSetFront, onReload, onLetterUpdated, onDelete, onPrev, onNext, hasPrev, hasNext, position, total }) {
  const others = (letters || []).filter((l) => l.id !== active?.id);
  const parent = active?.reply_to ? (letters || []).find((l) => l.id === active.reply_to) : null;
  const [titleDraft, setTitleDraft] = useState("");
  const [landscape, setLandscape] = useState(false);
  const [metaDraft, setMetaDraft] = useState({ date_text: "", place: "", summary: "", date_user_note: "", signature: "", writer: "", writer_relation: "", pull_quote: "" });
  const [suggestions, setSuggestions] = useState([]);
  const [summarizing, setSummarizing] = useState(false);
  const regenerate = async () => {
    if (!active?.id) return;
    setSummarizing(true);
    try {
      const updated = await api.letters.summarize(active.id);
      onLetterUpdated?.(updated);
      toast.success("Samenvatting en highlights bijgewerkt");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bijwerken mislukt");
    } finally {
      setSummarizing(false);
    }
  };
  const scanRefs = useRef([]);
  const scansRef = useRef(null);
  const [highlightScan, setHighlightScan] = useState(null);
  const highlightTimer = useRef(null);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.target && /INPUT|TEXTAREA/.test(e.target.tagName)) return;
      if (e.key === "ArrowLeft" && hasPrev) onPrev?.();
      if (e.key === "ArrowRight" && hasNext) onNext?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, hasPrev, hasNext, onPrev, onNext]);

  const locateScan = (pageIndex) => {
    const el = scanRefs.current[pageIndex];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightScan(pageIndex);
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightScan(null), 1900);
  };

  const loadSuggestions = async () => {
    if (!active?.id) return;
    try {
      setSuggestions(await api.letters.suggestions(active.id));
    } catch {
      /* stil */
    }
  };
  useEffect(() => {
    setTitleDraft(active?.title || "");
    setLandscape(false);
    setMetaDraft({ date_text: active?.date_text || "", place: active?.place || "", summary: active?.summary || "", date_user_note: active?.date_user_note || "", signature: active?.signature || "", writer: active?.writer || "", writer_relation: active?.writer_relation || "", pull_quote: active?.pull_quote || "" });
    setSuggestions([]);
    if (active?.id) loadSuggestions();
    setEditSuggestions([]);
    setProposeOpen(false);
    setEditingTx(false);
    if (active?.id) loadEditSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  const onMainImgLoad = (e) => setLandscape(e.target.naturalWidth > e.target.naturalHeight * 1.05);
  const frontInputRef = useRef(null);
  const pickFront = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Kies een afbeelding"); return; }
    try {
      const dataUrl = await fileToResizedDataUrl(file);
      onSetFront(active.id, dataUrl);
    } catch {
      toast.error("Kon afbeelding niet verwerken");
    }
  };
  const applySuggestion = async (sid) => {
    try {
      const updated = await api.letters.applySuggestion(active.id, sid);
      onLetterUpdated?.(updated);
      await loadSuggestions();
      toast.success("Suggestie overgenomen in de tekst");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Overnemen mislukt");
    }
  };
  const deleteSuggestion = async (sid) => {
    try {
      await api.letters.deleteSuggestion(active.id, sid);
      await loadSuggestions();
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const [editSuggestions, setEditSuggestions] = useState([]);
  const loadEditSuggestions = async () => {
    if (!active?.id) return;
    try { setEditSuggestions(await api.letters.editSuggestions(active.id)); } catch { /* stil */ }
  };
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeOriginal, setProposeOriginal] = useState("");
  const [proposeText, setProposeText] = useState("");
  const [proposeNote, setProposeNote] = useState("");
  const [editingTx, setEditingTx] = useState(false);
  const [txDraft, setTxDraft] = useState("");

  const startPropose = () => {
    const sel = (typeof window !== "undefined" && window.getSelection ? window.getSelection().toString() : "").trim();
    setProposeOriginal(sel);
    setProposeText(sel);
    setProposeNote("");
    setProposeOpen(true);
  };
  const submitPropose = async () => {
    const original = proposeOriginal.trim();
    const suggestion = proposeText.trim();
    if (!original) { toast.error("Selecteer of typ eerst de tekst die volgens jou niet klopt"); return; }
    if (!suggestion) { toast.error("Geef je voorgestelde tekst op"); return; }
    try {
      await api.letters.addEditSuggestion(active.id, { original, suggestion, note: proposeNote.trim() });
      setProposeOpen(false);
      await loadEditSuggestions();
      toast.success("Je voorgestelde aanpassing is doorgestuurd naar de beheerder");
    } catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
  };
  const approveEdit = async (sid) => {
    try {
      const updated = await api.letters.approveEditSuggestion(active.id, sid);
      onLetterUpdated?.(updated);
      await loadEditSuggestions();
      toast.success("Aanpassing goedgekeurd en toegepast");
    } catch (e) { toast.error(e?.response?.data?.detail || "Goedkeuren mislukt"); }
  };
  const rejectEdit = async (sid) => {
    try { await api.letters.rejectEditSuggestion(active.id, sid); await loadEditSuggestions(); }
    catch { toast.error("Afwijzen mislukt"); }
  };
  const startEditTx = () => { setTxDraft(stripMarkers(active.transcription || "")); setEditingTx(true); };
  const saveTx = async () => {
    try {
      const updated = await api.letters.updateTranscription(active.id, txDraft);
      onLetterUpdated?.(updated);
      setEditingTx(false);
      toast.success("Transcriptie opgeslagen");
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };

  const titleChanged = active && titleDraft.trim() !== (active.title || "");
  const metaChanged = active && (
    metaDraft.date_text !== (active.date_text || "") ||
    metaDraft.place !== (active.place || "") ||
    metaDraft.summary !== (active.summary || "") ||
    metaDraft.date_user_note !== (active.date_user_note || "") ||
    metaDraft.signature !== (active.signature || "") ||
    metaDraft.writer !== (active.writer || "") ||
    metaDraft.writer_relation !== (active.writer_relation || "") ||
    metaDraft.pull_quote !== (active.pull_quote || "")
  );
  return (
    <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
      <DialogContent className="max-h-[90vh] overflow-hidden bg-[var(--paper)] sm:max-w-4xl" data-testid="history-detail-dialog">
        {active && (
          <>
            <DialogHeader>
              <DialogTitle className="sr-only">{active.title || "Naamloze brief"}</DialogTitle>
              <DialogDescription className="sr-only">Details en transcriptie van de brief</DialogDescription>
              <div className="flex items-center gap-2 pr-8">
                {isAdmin ? (
                  <>
                    <input
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      placeholder="Titel toevoegen…"
                      data-testid="detail-title-input"
                      className="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1 py-0.5 font-serif text-2xl text-[var(--ink)] outline-none hover:border-[var(--border-subtle)] focus:border-[var(--brand)] focus:bg-[var(--surface)]"
                    />
                    {titleChanged && (
                      <button
                        onClick={() => onRename(active.id, titleDraft.trim())}
                        data-testid="detail-title-save"
                        className="shrink-0 rounded-md bg-[var(--brand)] px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                      >
                        Opslaan
                      </button>
                    )}
                  </>
                ) : (
                  <h2 className="min-w-0 flex-1 font-serif text-2xl text-[var(--ink)]" data-testid="detail-title-readonly">{active.title || "Naamloze brief"}</h2>
                )}
                {isAdmin && (
                  <button
                    onClick={() => onDelete?.(active.id)}
                    data-testid="detail-delete-btn"
                    title="Brief verwijderen"
                    className="flex shrink-0 items-center gap-1.5 rounded-md border border-red-300 px-2.5 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Verwijder
                  </button>
                )}
              </div>
            </DialogHeader>

            <div className="mb-1 flex items-center justify-between gap-2">
              <button
                onClick={onPrev}
                disabled={!hasPrev}
                data-testid="detail-prev-btn"
                className="flex items-center gap-1.5 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] px-3 py-1.5 text-xs font-semibold text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" /> Vorige
              </button>
              {position > 0 && total > 0 && (
                <span data-testid="detail-position" className="font-mono text-xs text-[var(--ink-3)]">{position} van {total}</span>
              )}
              <button
                onClick={onNext}
                disabled={!hasNext}
                data-testid="detail-next-btn"
                className="flex items-center gap-1.5 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] px-3 py-1.5 text-xs font-semibold text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Volgende <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="grid max-h-[78vh] grid-cols-1 gap-4 overflow-auto md:grid-cols-2">
              <div ref={scansRef} className="space-y-2 md:max-h-[78vh] md:self-start md:overflow-auto md:pr-1">
                <input ref={frontInputRef} type="file" accept="image/*" className="hidden" data-testid="detail-front-input" onChange={(e) => { pickFront(e.target.files?.[0]); e.target.value = ""; }} />
                {active.front_image ? (
                  <div className="overflow-hidden rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-muted)]">
                    <div className="flex flex-wrap items-center justify-between gap-1 border-b border-[var(--border-subtle)] px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
                      <span className="flex items-center gap-1"><ImageIcon className="h-3 w-3" /> Voorkant</span>
                      {isAdmin && (
                        <span className="flex items-center gap-2 normal-case tracking-normal">
                          <button onClick={() => onRotateFront(active.id)} data-testid="detail-rotate-front" title="Voorkant 90° draaien" className="flex items-center gap-1 text-[var(--ink-2)] transition-colors hover:text-[var(--brand)]"><RotateCw className="h-3 w-3" /> Draaien</button>
                          <button onClick={() => frontInputRef.current?.click()} data-testid="detail-replace-front" title="Voorkant vervangen" className="flex items-center gap-1 text-[var(--ink-2)] transition-colors hover:text-[var(--brand)]"><ImageIcon className="h-3 w-3" /> Vervangen</button>
                          <button onClick={() => { if (window.confirm("Voorkant van deze ansichtkaart verwijderen?")) onSetFront(active.id, ""); }} data-testid="detail-delete-front" title="Voorkant verwijderen" className="flex items-center gap-1 text-[var(--ink-2)] transition-colors hover:text-red-500"><Trash2 className="h-3 w-3" /> Verwijderen</button>
                        </span>
                      )}
                    </div>
                    <img src={active.front_image} alt="Voorkant" className="w-full" />
                  </div>
                ) : isAdmin && active.doc_type === "ansichtkaart" ? (
                  <button onClick={() => frontInputRef.current?.click()} data-testid="detail-add-front" className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-[var(--border-active)] bg-[var(--surface-muted)] px-3 py-3 text-xs font-medium text-[var(--ink-2)] transition-colors hover:border-[var(--brand)] hover:text-[var(--brand)]">
                    <ImageIcon className="h-4 w-4" /> Voorkant (fotokant) toevoegen
                  </button>
                ) : null}
                {(active.images || []).map((img, i) => (
                  <ScanCard
                    key={i}
                    img={img}
                    index={i}
                    total={active.images.length}
                    title={active.title}
                    isAdmin={isAdmin}
                    onRotate={() => onRotateImage(active.id, i)}
                    onLoad={i === 0 ? onMainImgLoad : undefined}
                    cardRef={(el) => { scanRefs.current[i] = el; }}
                    highlight={highlightScan === i}
                  />
                ))}
              </div>
              <div>
                <div className="mb-3 grid grid-cols-2 gap-2 text-xs">
                  <Meta icon={DOC_ICONS[active.doc_type] || FileText} label={active.is_typed ? "Soort (getypt)" : "Soort"} value={DOC_TYPE_LABEL[active.doc_type] || "Brief"} />
                  {isAdmin ? (
                    <EditMeta icon={Calendar} label="Datum" value={metaDraft.date_text} onChange={(v) => setMetaDraft((m) => ({ ...m, date_text: v }))} placeholder="bijv. 15 Januari 1953" testid="detail-date-input" />
                  ) : (
                    <Meta icon={Calendar} label="Datum" value={active.date_text || active.date_iso || "—"} />
                  )}
                  {isAdmin ? (
                    <EditMeta icon={MapPin} label="Plaats" value={metaDraft.place} onChange={(v) => setMetaDraft((m) => ({ ...m, place: v }))} placeholder="bijv. Arnhem" testid="detail-place-input" />
                  ) : (
                    active.place ? <Meta icon={MapPin} label="Plaats" value={active.place} /> : null
                  )}
                  {isAdmin ? (
                    <EditMeta icon={User} label="Schrijver" value={metaDraft.writer} onChange={(v) => setMetaDraft((m) => ({ ...m, writer: v }))} placeholder="bijv. Pieter, Ineke of een andere naam" testid="detail-writer-input" />
                  ) : (
                    <Meta icon={User} label="Schrijver" value={active.writer || writerKey(active)} />
                  )}
                  {isAdmin ? (
                    <EditMeta icon={User} label="Relatie tot Pieter/Ineke" value={metaDraft.writer_relation} onChange={(v) => setMetaDraft((m) => ({ ...m, writer_relation: v }))} placeholder="bijv. zus van Ineke (voor andere schrijvers)" testid="detail-writer-relation-input" />
                  ) : (
                    active.writer_relation ? <Meta icon={User} label="Relatie tot Pieter/Ineke" value={active.writer_relation} /> : null
                  )}
                  {isAdmin ? (
                    <EditMeta icon={PenLine} label="Ondertekening" value={metaDraft.signature} onChange={(v) => setMetaDraft((m) => ({ ...m, signature: v }))} placeholder="bijv. Uw zoon, Hendrik" testid="detail-signature-input" />
                  ) : (
                    active.signature ? <Meta icon={PenLine} label="Ondertekening" value={active.signature} /> : null
                  )}
                  {active.date_note && <div className="col-span-2"><Meta icon={Calendar} label="AI-opmerking bij datum" value={active.date_note} /></div>}
                  {isAdmin ? (
                    <div className="col-span-2">
                      <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]"><PenLine className="h-3 w-3" /> Mijn opmerking bij datum</span>
                      <input
                        value={metaDraft.date_user_note}
                        onChange={(e) => setMetaDraft((m) => ({ ...m, date_user_note: e.target.value }))}
                        placeholder="bijv. datum onduidelijk/ontbreekt — ik heb op basis van de context gegokt"
                        data-testid="detail-date-user-note-input"
                        className="mt-1 w-full rounded-md border border-transparent bg-transparent px-1 py-0.5 text-sm text-[var(--ink)] outline-none hover:border-[var(--border-subtle)] focus:border-[var(--brand)] focus:bg-[var(--surface)]"
                      />
                    </div>
                  ) : (
                    active.date_user_note ? <div className="col-span-2"><Meta icon={PenLine} label="Opmerking bij datum" value={active.date_user_note} /></div> : null
                  )}
                  {active.salutation && <div className="col-span-2"><Meta icon={MessageSquareQuote} label="Aanhef" value={active.salutation} /></div>}
                </div>

                {(isAdmin || active.summary) && (
                  <div className="mb-3 rounded-lg border border-[var(--brand)]/25 bg-[var(--brand)]/5 p-2.5" data-testid="detail-summary">
                    <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[var(--brand)]"><MessageSquareQuote className="h-3 w-3" /> Samenvatting</span>
                    {isAdmin ? (
                      <textarea
                        value={metaDraft.summary}
                        onChange={(e) => setMetaDraft((m) => ({ ...m, summary: e.target.value }))}
                        rows={3}
                        placeholder="Samenvatting toevoegen…"
                        data-testid="detail-summary-input"
                        className="mt-1 w-full resize-y rounded-md border border-transparent bg-transparent p-1 text-sm italic text-[var(--ink)] outline-none hover:border-[var(--brand)]/30 focus:border-[var(--brand)] focus:bg-[var(--paper)]"
                      />
                    ) : (
                      <p className="mt-1 text-sm italic text-[var(--ink)]">{active.summary}</p>
                    )}
                  </div>
                )}

                {(isAdmin || active.pull_quote) && (
                  <div className="mb-3 rounded-lg border border-[var(--brand)]/25 bg-[var(--brand)]/5 p-2.5" data-testid="detail-pull-quote">
                    <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[var(--brand)]"><Quote className="h-3 w-3" /> Uitgelichte quote</span>
                    {isAdmin ? (
                      <textarea
                        value={metaDraft.pull_quote}
                        onChange={(e) => setMetaDraft((m) => ({ ...m, pull_quote: e.target.value }))}
                        rows={2}
                        placeholder="Een korte, pakkende zin uit de brief die eruit mag springen…"
                        data-testid="detail-pull-quote-input"
                        className="mt-1 w-full resize-y rounded-md border border-transparent bg-transparent p-1 text-sm italic text-[var(--ink)] outline-none hover:border-[var(--brand)]/30 focus:border-[var(--brand)] focus:bg-[var(--paper)]"
                      />
                    ) : (
                      <p className="mt-1 text-sm italic text-[var(--ink)]">“{active.pull_quote}”{active.pull_quote_by ? <span className="ml-1 not-italic font-mono text-[10px] uppercase tracking-wider text-[var(--brand)]">— {active.pull_quote_by}</span> : null}</p>
                    )}
                  </div>
                )}

                {(isAdmin || (active.highlights?.length > 0)) && (
                  <div className="mb-3 rounded-lg border border-[var(--brand)]/25 bg-[var(--brand)]/5 p-2.5" data-testid="detail-highlights">
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[var(--brand)]"><Sparkles className="h-3 w-3" /> Opvallende momenten</span>
                      {isAdmin && (
                        <button
                          onClick={regenerate}
                          disabled={summarizing}
                          data-testid="detail-regenerate-highlights"
                          className="flex items-center gap-1 rounded-md border border-[var(--brand)]/40 bg-[var(--paper)] px-2 py-0.5 text-[11px] font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--brand)]/10 disabled:opacity-50"
                        >
                          {summarizing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                          {active.highlights?.length > 0 ? "Opnieuw" : "Genereren"}
                        </button>
                      )}
                    </div>
                    {active.highlights?.length > 0 ? (
                      <HighlightTags items={active.highlights} className="mt-2" max={12} />
                    ) : (
                      <p className="mt-1 text-xs italic text-[var(--ink-3)]">Nog geen highlights — klik op "Genereren" om opvallende momenten uit de brief te halen.</p>
                    )}
                  </div>
                )}

                {isAdmin && metaChanged && (
                  <button
                    onClick={() => onSaveMeta(active.id, { date_text: metaDraft.date_text.trim(), place: metaDraft.place.trim(), summary: metaDraft.summary.trim(), date_user_note: metaDraft.date_user_note.trim(), signature: metaDraft.signature.trim(), writer: metaDraft.writer.trim(), writer_relation: metaDraft.writer_relation.trim(), pull_quote: metaDraft.pull_quote.trim(), pull_quote_by: active.pull_quote_by || (metaDraft.writer.trim().split(" ")[0] || "") })}
                    data-testid="detail-meta-save"
                    className="mb-3 w-full rounded-md bg-[var(--brand)] px-3 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                  >
                    Wijzigingen opslaan
                  </button>
                )}

                {active.tone && (
                  <div className="mb-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-2.5" data-testid="detail-tone">
                    <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]"><Palette className="h-3 w-3" /> Toon / sfeer</span>
                    <p className="mt-0.5 text-sm text-[var(--ink)]">{active.tone}</p>
                  </div>
                )}

                {(suggestions.length > 0 || hasSlots(active.transcription)) && !editingTx && (
                  <div className="mb-2 flex items-start gap-2 rounded-lg border border-[var(--amber-border)]/60 bg-[var(--amber-bg)] px-3 py-2 text-xs text-[var(--amber-text)]" data-testid="suggestion-legend">
                    <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>Woorden gemarkeerd als <span className="unreadable">xx</span> kon de lezer niet ontcijferen. Klik erop om een suggestie te doen — alle suggesties zijn voor iedereen zichtbaar{isAdmin ? " en jij kunt ze met één klik overnemen" : ""}.</span>
                  </div>
                )}

                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Transcriptie</span>
                  {!editingTx && (
                    <div className="flex items-center gap-2">
                      {!proposeOpen && (
                        <button onClick={startPropose} data-testid="propose-edit-btn" className="flex items-center gap-1.5 rounded-lg border border-[var(--border-subtle)] px-2.5 py-1.5 text-xs font-semibold text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-muted)]">
                          <PenLine className="h-3.5 w-3.5" /> Aanpassing voorstellen
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={startEditTx} data-testid="edit-transcription-btn" className="flex items-center gap-1.5 rounded-lg border border-[var(--brand)]/40 bg-[var(--paper)] px-2.5 py-1.5 text-xs font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--brand)]/10">
                          <PenLine className="h-3.5 w-3.5" /> Bewerk transcriptie
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {proposeOpen && !editingTx && (
                  <div className="mb-3 rounded-lg border border-[var(--brand)]/30 bg-[var(--surface-muted)] p-3" data-testid="propose-edit-form">
                    <p className="mb-2 text-xs text-[var(--ink-2)]">Markeer in de tekst wat volgens jou niet klopt en pas het hieronder aan. De beheerder beoordeelt je voorstel.</p>
                    <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">Oorspronkelijke tekst</label>
                    <textarea value={proposeOriginal} onChange={(e) => setProposeOriginal(e.target.value)} rows={2} data-testid="propose-original" placeholder="selecteer tekst hierboven, of typ hem hier" className="mb-2 w-full rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] px-2.5 py-1.5 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]" />
                    <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">Jouw voorgestelde tekst</label>
                    <textarea value={proposeText} onChange={(e) => setProposeText(e.target.value)} rows={2} data-testid="propose-suggestion" placeholder="hoe het volgens jou moet zijn" className="mb-2 w-full rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] px-2.5 py-1.5 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]" />
                    <input value={proposeNote} onChange={(e) => setProposeNote(e.target.value)} data-testid="propose-note" placeholder="toelichting (optioneel)" className="mb-2 h-9 w-full rounded-md border border-[var(--border-subtle)] bg-[var(--paper)] px-2.5 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]" />
                    <div className="flex gap-2">
                      <button onClick={submitPropose} data-testid="propose-submit" className="flex items-center gap-1.5 rounded-lg bg-[var(--brand)] px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90"><Send className="h-3.5 w-3.5" /> Voorstel versturen</button>
                      <button onClick={() => setProposeOpen(false)} data-testid="propose-cancel" className="rounded-lg border border-[var(--border-subtle)] px-3 py-1.5 text-sm text-[var(--ink-2)] hover:bg-[var(--surface-muted)]">Annuleren</button>
                    </div>
                  </div>
                )}

                {editingTx ? (
                  <div data-testid="transcription-editor">
                    <textarea value={txDraft} onChange={(e) => setTxDraft(e.target.value)} rows={16} data-testid="transcription-textarea" className="w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--paper)] p-3 font-serif text-[15px] leading-7 text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]" />
                    <div className="mt-2 flex gap-2">
                      <button onClick={saveTx} data-testid="transcription-save" className="flex items-center gap-1.5 rounded-lg bg-[var(--brand)] px-3 py-2 text-sm font-semibold text-white hover:opacity-90"><Check className="h-4 w-4" /> Opslaan</button>
                      <button onClick={() => setEditingTx(false)} data-testid="transcription-cancel" className="rounded-lg border border-[var(--border-subtle)] px-3 py-2 text-sm text-[var(--ink-2)] hover:bg-[var(--surface-muted)]">Annuleren</button>
                    </div>
                  </div>
                ) : (
                  <SuggestibleText
                    letterId={active.id}
                    text={active.transcription}
                    pageCount={(active.images || []).length}
                    suggestions={suggestions}
                    isAdmin={isAdmin}
                    onReloadSuggestions={loadSuggestions}
                    onApply={applySuggestion}
                    onDeleteSuggestion={deleteSuggestion}
                    onLocate={locateScan}
                  />
                )}

                {editSuggestions.length > 0 && !editingTx && (
                  <div className="mt-4 rounded-lg border border-[var(--brand)]/25 bg-[var(--surface-muted)] p-3" data-testid="edit-suggestions">
                    <p className="mb-2 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]"><Lightbulb className="h-3.5 w-3.5" /> Voorgestelde aanpassingen ({editSuggestions.length})</p>
                    <ul className="space-y-2">
                      {editSuggestions.map((s) => (
                        <li key={s.id} data-testid={`edit-suggestion-${s.id}`} className="rounded-md border border-[var(--border-subtle)] bg-[var(--surface)] p-2.5">
                          <div className="text-sm">
                            <span className="rounded bg-red-100 px-1 text-red-700 line-through">{s.original}</span>
                            <CornerDownRight className="mx-1 inline h-3.5 w-3.5 text-[var(--ink-3)]" />
                            <span className="rounded bg-green-100 px-1 font-semibold text-green-800">{s.suggestion}</span>
                          </div>
                          {s.note && <p className="mt-1 text-xs italic text-[var(--ink-2)]">“{s.note}”</p>}
                          <div className="mt-1.5 flex items-center justify-between">
                            <span className="font-mono text-[10px] text-[var(--ink-3)]">voorgesteld door {s.author || "iemand"}</span>
                            {isAdmin && (
                              <div className="flex items-center gap-1.5">
                                <button onClick={() => approveEdit(s.id)} data-testid={`edit-suggestion-approve-${s.id}`} title="Goedkeuren en toepassen" className="flex h-7 items-center gap-1 rounded-md bg-green-600 px-2 text-xs font-semibold text-white hover:opacity-90"><Check className="h-3.5 w-3.5" /> Goedkeuren</button>
                                <button onClick={() => rejectEdit(s.id)} data-testid={`edit-suggestion-reject-${s.id}`} title="Afwijzen" className="flex h-7 w-7 items-center justify-center rounded-md border border-[var(--border-subtle)] text-[var(--ink-3)] hover:border-red-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {active.notes && <p className="mt-3 rounded-lg bg-[var(--surface-muted)] p-2.5 text-sm text-[var(--ink-2)]">{active.notes}</p>}
                <div className="mt-4 flex gap-2">
                  <button onClick={() => copy(active.transcription)} className="flex items-center gap-1.5 rounded-lg border border-[var(--border-subtle)] px-3 py-2 text-sm text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"><Copy className="h-4 w-4" /> Kopiëren</button>
                  <button onClick={() => download(active)} className="flex items-center gap-1.5 rounded-lg border border-[var(--border-subtle)] px-3 py-2 text-sm text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"><Download className="h-4 w-4" /> Download</button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const SLOT_RE = /(?<![0-9A-Za-zÀ-ÿ])x{2,}(?![0-9A-Za-zÀ-ÿ])/gi;
const hasSlots = (text) => SLOT_RE.test((text || "").replace(/[⟦⟧]/g, "")) && (SLOT_RE.lastIndex = 0, true);

const whereLabel = (within) =>
  within < 0.34 ? "bovenaan de pagina" : within < 0.67 ? "halverwege de pagina" : "onderaan de pagina";

// Bouw de schone transcriptie en de startposities van elke pagina uit <<<PAGINA>>>-markeringen.
function splitPages(text) {
  const rawU = (text || "").replace(/⟦([^⟧]*)⟧/g, "$1");
  const re = new RegExp(PAGE_MARK_RE.source, "g");
  let clean = "", starts = [0], li = 0, m;
  while ((m = re.exec(rawU)) !== null) {
    clean += rawU.slice(li, m.index);
    starts.push(clean.length);
    li = re.lastIndex;
  }
  clean += rawU.slice(li);
  return { clean, starts, hasMarkers: starts.length > 1 };
}

// Bepaal op welke pagina (en ongeveer waar) een onleesbaar woord staat.
function locateSlot(index, clean, starts, hasMarkers, fallbackPages) {
  if (hasMarkers) {
    let j = 0;
    while (j + 1 < starts.length && starts[j + 1] <= index) j += 1;
    const pageStart = starts[j];
    const pageEnd = j + 1 < starts.length ? starts[j + 1] : clean.length;
    const within = pageEnd > pageStart ? (index - pageStart) / (pageEnd - pageStart) : 0;
    return { page: j + 1, pageIndex: j, pageCount: starts.length, where: whereLabel(within), accurate: true };
  }
  const pages = fallbackPages && fallbackPages > 0 ? fallbackPages : 1;
  const total = clean.length || 1;
  const frac = Math.min(0.9999, Math.max(0, index / total));
  const pageIdx = Math.min(pages - 1, Math.floor(frac * pages));
  const within = frac * pages - pageIdx;
  return { page: pageIdx + 1, pageIndex: pageIdx, pageCount: pages, where: whereLabel(within), accurate: false };
}

function ScanCard({ img, index, total, title, isAdmin, onRotate, onLoad, cardRef, highlight }) {
  const [zoom, setZoom] = useState(1);
  return (
    <div
      ref={cardRef}
      data-testid={`detail-scan-${index}`}
      className={`overflow-hidden rounded-lg border bg-[var(--surface-muted)] paper-grain transition-all duration-300 ${
        highlight ? "border-[var(--brand)] ring-2 ring-[var(--brand)]/60 shadow-lg" : "border-[var(--border-subtle)]"
      }`}
    >
      <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
        <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> Scan{total > 1 ? ` ${index + 1}` : ""}</span>
        <span className="flex items-center gap-2 normal-case tracking-normal">
          <button onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))} disabled={zoom <= 1} data-testid={`detail-zoom-out-${index}`} title="Uitzoomen" className="flex items-center text-[var(--ink-2)] transition-colors hover:text-[var(--brand)] disabled:opacity-30"><ZoomOut className="h-3.5 w-3.5" /></button>
          <span className="w-9 text-center tabular-nums text-[var(--ink-3)]" data-testid={`detail-zoom-level-${index}`}>{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} disabled={zoom >= 4} data-testid={`detail-zoom-in-${index}`} title="Inzoomen" className="flex items-center text-[var(--ink-2)] transition-colors hover:text-[var(--brand)] disabled:opacity-30"><ZoomIn className="h-3.5 w-3.5" /></button>
          {isAdmin && (
            <button onClick={onRotate} data-testid={`detail-rotate-image-${index}`} title="Scan 90° draaien" className="ml-1 flex items-center gap-1 text-[var(--ink-2)] transition-colors hover:text-[var(--brand)]"><RotateCw className="h-3 w-3" /> Draaien</button>
          )}
        </span>
      </div>
      <div className="max-h-[70vh] overflow-auto">
        <img
          src={img}
          alt={`${title || "scan"} ${index + 1}`}
          onLoad={onLoad}
          style={{ transform: `scale(${zoom})`, transformOrigin: "top left", transition: "transform 0.2s ease" }}
          className="w-full"
        />
      </div>
    </div>
  );
}

function sentenceAround(text, start, end) {
  const bounds = ".!?\n";
  let s = start;
  while (s > 0 && !bounds.includes(text[s - 1])) s--;
  let e = end;
  while (e < text.length && !bounds.includes(text[e])) e++;
  if (e < text.length) e++;
  return {
    pre: text.slice(s, start).replace(/^\s+/, ""),
    word: text.slice(start, end),
    post: text.slice(end, e).replace(/\s+$/, ""),
  };
}

function SuggestibleText({ letterId, text, pageCount, suggestions, isAdmin, onReloadSuggestions, onApply, onDeleteSuggestion, onLocate }) {
  const { clean, starts, hasMarkers } = splitPages(text);
  const bySlot = {};
  (suggestions || []).forEach((s) => { (bySlot[s.slot_index] = bySlot[s.slot_index] || []).push(s); });
  const parts = [];
  const re = new RegExp(SLOT_RE.source, "gi");
  let last = 0, m, slot = -1;
  while ((m = re.exec(clean)) !== null) {
    if (m.index > last) parts.push({ t: "text", v: clean.slice(last, m.index) });
    slot += 1;
    parts.push({
      t: "slot",
      v: m[0],
      slot,
      context: sentenceAround(clean, m.index, re.lastIndex),
      location: locateSlot(m.index, clean, starts, hasMarkers, pageCount),
    });
    last = re.lastIndex;
  }
  if (last < clean.length) parts.push({ t: "text", v: clean.slice(last) });

  return (
    <p className="whitespace-pre-wrap font-serif text-[15px] leading-7 text-[var(--ink)]" data-testid="detail-transcription">
      {parts.map((p, i) =>
        p.t === "text" ? (
          <span key={i}>{p.v}</span>
        ) : (
          <SlotChip
            key={i}
            letterId={letterId}
            slot={p.slot}
            token={p.v}
            context={p.context}
            location={p.location}
            slotSuggestions={bySlot[p.slot] || []}
            isAdmin={isAdmin}
            onReloadSuggestions={onReloadSuggestions}
            onApply={onApply}
            onDeleteSuggestion={onDeleteSuggestion}
            onLocate={onLocate}
          />
        )
      )}
    </p>
  );
}

function SlotChip({ letterId, slot, token, context, location, slotSuggestions, isAdmin, onReloadSuggestions, onApply, onDeleteSuggestion, onLocate }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    const t = val.trim();
    if (!t) return;
    setBusy(true);
    try {
      await api.letters.addSuggestion(letterId, { slot_index: slot, token, suggestion: t });
      setVal("");
      await onReloadSuggestions();
      toast.success("Suggestie toegevoegd");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toevoegen mislukt");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Popover onOpenChange={(o) => { if (o && location && onLocate) onLocate(location.pageIndex); }}>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          data-testid={`slot-${slot}`}
          title="Onleesbaar woord — klik om de vindplaats te zien en een suggestie te doen"
          className="mx-0.5 inline-flex cursor-pointer items-center gap-1 rounded-md bg-[#dc2626] px-1.5 py-0.5 align-baseline text-xs font-bold text-white shadow-sm ring-1 ring-red-700/40 transition-colors hover:bg-[#b91c1c]"
        >
          <Lightbulb className="h-3 w-3" />
          {token}
          {slotSuggestions.length > 0 && (
            <span className="rounded-full bg-white px-1 text-[9px] font-extrabold text-[#dc2626]" data-testid={`slot-count-${slot}`}>{slotSuggestions.length}</span>
          )}
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 bg-[var(--paper)] p-3" data-testid={`slot-popover-${slot}`}>
        <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
          <Lightbulb className="h-3.5 w-3.5 text-[var(--brand)]" /> Onleesbaar woord
        </div>
        {location && (
          <div className="mb-3 rounded-md border border-[var(--brand)]/30 bg-[var(--brand)]/5 p-2" data-testid={`slot-location-${slot}`}>
            <div className="flex items-start gap-1.5">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--brand)]" />
              <span className="text-xs text-[var(--ink)]">
                {location.accurate ? "Op" : "Vermoedelijk op"} <span className="font-semibold">pagina {location.page}{location.pageCount > 1 ? ` van ${location.pageCount}` : ""}</span>, ongeveer <span className="font-semibold">{location.where}</span>.
              </span>
            </div>
            {onLocate && (
              <button
                onClick={() => onLocate(location.pageIndex)}
                data-testid={`slot-locate-${slot}`}
                className="mt-2 flex items-center gap-1 rounded-md border border-[var(--brand)]/40 bg-[var(--paper)] px-2 py-1 text-[11px] font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--brand)]/10"
              >
                <ImageIcon className="h-3 w-3" /> Toon deze pagina in de scan
              </button>
            )}
          </div>
        )}
        {context && (context.pre || context.post) && (
          <div className="mb-3 rounded-md border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-2" data-testid={`slot-context-${slot}`}>
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">Waar in de brief</span>
            <p className="font-serif text-sm leading-6 text-[var(--ink)]">
              …{context.pre}
              <mark className="mx-0.5 rounded bg-[#dc2626] px-1 font-bold text-white">{context.word}</mark>
              {context.post}…
            </p>
            <p className="mt-1 text-[10px] text-[var(--ink-3)]">Zoek deze zin terug in de scan hiernaast om het woord te vinden.</p>
          </div>
        )}
        <div className="mb-2 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">Suggesties voor "{token}"</div>
        {slotSuggestions.length === 0 ? (
          <p className="mb-2 text-xs text-[var(--ink-3)]">Nog geen suggesties. Doe de eerste!</p>
        ) : (
          <ul className="mb-2 space-y-1.5">
            {slotSuggestions.map((s) => (
              <li key={s.id} data-testid={`suggestion-${s.id}`} className="flex items-start justify-between gap-2 rounded-md border border-[var(--border-subtle)] bg-[var(--surface)] px-2 py-1.5">
                <div className="min-w-0">
                  <p className="break-words text-sm font-semibold text-[var(--ink)]">{s.suggestion}</p>
                  <p className="truncate font-mono text-[10px] text-[var(--ink-3)]">{s.author || "Onbekend"}</p>
                </div>
                {isAdmin && (
                  <div className="flex shrink-0 items-center gap-1">
                    <button onClick={() => onApply(s.id)} data-testid={`suggestion-apply-${s.id}`} title="Overnemen in de tekst" className="flex h-7 w-7 items-center justify-center rounded-md bg-green-600 text-white hover:opacity-90"><Check className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onDeleteSuggestion(s.id)} data-testid={`suggestion-delete-${s.id}`} title="Suggestie verwijderen" className="flex h-7 w-7 items-center justify-center rounded-md border border-[var(--border-subtle)] text-[var(--ink-3)] hover:border-red-300 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center gap-1.5">
          <input
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="Jouw suggestie…"
            data-testid={`slot-input-${slot}`}
            className="h-9 flex-1 rounded-md border border-[var(--border-subtle)] bg-[var(--surface)] px-2 text-sm text-[var(--ink)] outline-none focus:ring-2 focus:ring-[var(--brand)]"
          />
          <button onClick={add} disabled={busy || !val.trim()} data-testid={`slot-add-${slot}`} className="flex h-9 items-center gap-1 rounded-md bg-[var(--brand)] px-2.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DocBadge({ l, className = "" }) {
  const type = l.doc_type || "handgeschreven";
  const Icon = DOC_ICONS[type] || FileText;
  const label = DOC_TYPE_LABEL[type] || "Brief";
  return (
    <span className={`flex items-center gap-1 ${className}`} data-testid={`doc-badge-${l.id}`} title={label}>
      <Icon className="h-3 w-3" />
      {label}
      {l.is_typed && type !== "getypt" && <Keyboard className="h-3 w-3 text-[var(--brand)]" title="Getypte tekst" />}
    </span>
  );
}

function ToneTag({ tone, testid }) {
  if (!tone) return null;
  return (
    <span
      data-testid={testid}
      className="mt-2 inline-flex items-center gap-1 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-2.5 py-0.5 text-[11px] font-medium text-[var(--ink-2)]"
    >
      <Palette className="h-3 w-3 text-[var(--brand)]" /> {tone}
    </span>
  );
}

function EditMeta({ icon: Icon, label, value, onChange, placeholder, testid }) {
  return (
    <div className="rounded-lg bg-[var(--surface-muted)] px-2.5 py-1.5">
      <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]"><Icon className="h-3 w-3" /> {label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-0.5 w-full rounded border border-transparent bg-transparent text-sm text-[var(--ink)] outline-none hover:border-[var(--border-subtle)] focus:border-[var(--brand)] focus:bg-[var(--paper)]"
      />
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-[var(--surface-muted)] px-2.5 py-1.5">
      <span className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]"><Icon className="h-3 w-3" /> {label}</span>
      <p className="mt-0.5 text-sm text-[var(--ink)]">{value}</p>
    </div>
  );
}

function Empty() {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--border-active)] py-20 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--surface-muted)] text-[var(--ink-3)]">
        <ScrollText className="h-6 w-6" />
      </div>
      <p className="mt-4 font-display font-semibold text-[var(--ink)]">Nog niets opgeslagen</p>
      <p className="mt-1 text-sm text-[var(--ink-3)]">Transcribeer een brief en sla hem op.</p>
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testid, allLabel }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 w-40 border-[var(--border-subtle)] bg-[var(--paper)] text-sm text-[var(--ink)]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        <SelectItem value="all" data-testid={`${testid}-all`}>{allLabel}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o} data-testid={`${testid}-${o}`}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SegBtn({ children, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
        active ? "bg-[var(--brand)] text-white" : "bg-[var(--surface)] text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"
      }`}
    >
      {children}
    </button>
  );
}

function IconBtn({ children, onClick, danger, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
        danger ? "text-[var(--ink-3)] hover:bg-red-50 hover:text-red-600" : "text-[var(--ink-2)] hover:bg-[var(--surface-muted)]"
      }`}
    >
      {children}
    </button>
  );
}
